import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { taskService } from '../services/taskService';
import { Task, UserLevel } from '../types';
import { useToast } from '../components/Toast';

const TasksPage: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [userLevel, setUserLevel] = useState<UserLevel | null>(null);
  const [loading, setLoading] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [tasksData, levelData] = await Promise.all([
        taskService.listTasks(undefined, 'system'),
        taskService.getMyLevel(),
      ]);
      setTasks(tasksData.tasks);
      setUserLevel(levelData);
    } catch (err: any) {
      showToast(err?.response?.data?.error || t('tasks.errors.loadData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCompleteTask = async (taskId: number) => {
    setCompletingTaskId(taskId);
    try {
      const result = await taskService.completeTask(taskId);
      setUserLevel(result.user_level);
      setTasks((prev) =>
        prev.map((task) => (task.id === taskId ? { ...task, completed: true, completed_at: result.user_task.completed_at } : task))
      );
      showToast(t('tasks.success.completed', { points: result.user_task.points_earned }), 'success');
      if (result.user_level.level > (userLevel?.level || 1)) {
        showToast(t('tasks.success.levelUp', { level: result.user_level.level }), 'success', 5000);
      }
    } catch (err: any) {
      showToast(err?.response?.data?.error || t('tasks.errors.completeTask'), 'error');
    } finally {
      setCompletingTaskId(null);
    }
  };

  const getTaskTypeLabel = (type: Task['task_type']) => {
    const typeKey = `tasks.types.${type}`;
    return t(typeKey, { defaultValue: type });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="text-center">{t('tasks.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold mb-6 text-white">{t('tasks.pageTitle')}</h1>
      <p className="text-white mb-6">{t('tasks.pageDescription')}</p>

      {/* User Level Card */}
      {userLevel && (
        <div className="bg-white shadow rounded-xl border border-gray-200 mb-6 p-6">
          <h2 className="text-xl font-semibold mb-4">{t('tasks.level.title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <div className="text-sm text-gray-600">{t('tasks.level.level')}</div>
              <div className="text-3xl font-bold text-primary-600">{userLevel.level}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">{t('tasks.level.totalPoints')}</div>
              <div className="text-3xl font-bold text-primary-600">{userLevel.total_points}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600">{t('tasks.level.pointsToNext')}</div>
              <div className="text-3xl font-bold text-primary-600">
                {userLevel.points_to_next || 0}
              </div>
            </div>
          </div>
          {userLevel.current_level_info && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-2">{t('tasks.level.unlockedFeatures')}</div>
              {userLevel.current_level_info.features && userLevel.current_level_info.features.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {userLevel.current_level_info.features.map((feature, i) => {
                    const label = t(`tasks.features.${feature}`, { defaultValue: feature });
                    return (
                      <span
                        key={i}
                        className="inline-flex items-center px-3 py-1 bg-gradient-to-r from-green-100 to-green-50 text-green-800 rounded-lg text-sm font-medium border border-green-200"
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="text-sm text-gray-500">{t('tasks.level.noFeatures')}</div>
              )}
            </div>
          )}
          {userLevel.next_level && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600">
                {t('tasks.level.nextLevel')}: <span className="font-medium">{userLevel.next_level.name || `Level ${userLevel.next_level.level_number}`}</span> (
                {userLevel.next_level.required_points} {t('tasks.level.points')})
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tasks List */}
      <div className="bg-white shadow rounded-xl border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">{t('tasks.list.title')}</h2>
        </div>
        <div className="p-4">
          {tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">{t('tasks.list.empty')}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className={`border rounded-lg p-4 ${task.completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
                    }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-semibold text-lg">{task.title}</h3>
                    {task.completed && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
                        ✓ {t('tasks.list.completed')}
                      </span>
                    )}
                  </div>
                  {task.description && (
                    <p className="text-sm text-gray-600 mb-3">{task.description}</p>
                  )}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                      {getTaskTypeLabel(task.task_type)}
                    </span>
                    <span className="text-lg font-bold text-primary-600">{task.points} {t('tasks.level.points')}</span>
                  </div>
                  {task.completed ? (
                    <div className="text-sm text-gray-500">
                      {t('tasks.list.completedAt')}: {task.completed_at ? new Date(task.completed_at).toLocaleDateString() : ''}
                    </div>
                  ) : (
                    <button
                      onClick={() => handleCompleteTask(task.id)}
                      disabled={completingTaskId === task.id}
                      className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {completingTaskId === task.id ? t('tasks.list.processing') : t('tasks.list.completeTask')}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TasksPage;
